import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, formatApiError, money, fmtDate } from "@/lib/api";
import { FilingTimeline } from "@/components/FilingTimeline";
import { StatusPill } from "@/components/StatusPill";
import { BillingCard } from "@/components/BillingCard";
import { DocumentsPanel } from "@/components/DocumentsPanel";
import { PolicyDocButtons } from "@/components/PolicyDocButtons";
import { CoiDialog } from "@/components/CoiDialog";
import { ClaimDialog } from "@/components/ClaimDialog";
import { RenewButton } from "@/components/RenewButton";
import { stateName, coverageLabel, levelLabel, limitLabel, FILING_LABEL } from "@/lib/constants";

const Item = ({ k, v, mono }) => (
  <div>
    <p className="text-xs uppercase tracking-wider text-slate-500">{k}</p>
    <p className={`mt-1 font-semibold ${mono ? "font-mono" : ""}`}>{v || "—"}</p>
  </div>
);

export default function PolicyDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    api.get(`/policies/${id}`).then((r) => setP(r.data)).catch((err) => {
      setMissing(true);
      if (err.response?.status !== 404) toast.error(formatApiError(err));
    });
  }, [id]);
  if (missing) {
    return (
      <div className="mx-auto max-w-xl px-4 py-24 text-center" data-testid="policy-not-found">
        <h1 className="text-2xl font-extrabold">Policy not found</h1>
        <p className="mt-2 text-slate-600">This policy doesn't exist or isn't on your account.</p>
        <Button asChild variant="outline" className="mt-6"><Link to="/dashboard">Back to my account</Link></Button>
      </div>
    );
  }
  if (!p) return <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-blue-700" /></div>;
  const ready = p.filing_status === "certificate_active";
  const upcoming = p.effective_date > new Date().toLocaleDateString("en-CA");
  return (
    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6" data-testid="policy-detail">
      <Link to="/dashboard" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900" data-testid="back-to-dashboard"><ArrowLeft className="mr-1 h-4 w-4" /> My account</Link>
      <div className="mt-6 overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="flex flex-col gap-4 border-b border-slate-200 bg-slate-50 p-6 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="font-mono text-sm text-slate-500" data-testid="policy-number">{p.policy_number}</p>
            <h1 className="mt-1 text-2xl font-extrabold tracking-tight">{coverageLabel(p.coverage_type)} · {stateName(p.state)}</h1>
            <div className="mt-2 flex flex-wrap gap-2 text-xs font-semibold">
              {upcoming && <span className="rounded-full bg-blue-50 px-2.5 py-1 text-blue-700" data-testid="policy-starts-label">Starts {fmtDate(p.effective_date)}</span>}
              {p.renewed_by && <Link to={`/policies/${p.renewed_by}`} className="rounded-full bg-emerald-50 px-2.5 py-1 text-emerald-700 hover:underline" data-testid="policy-renewed-label">Renewed · view new term</Link>}
              {p.comped && <span className="rounded-full bg-amber-50 px-2.5 py-1 text-amber-800" data-testid="policy-comped-label">Issued at no charge</span>}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <StatusPill status={p.filing_status} testId="policy-filing-status" />
            <RenewButton policy={p} />
            <PolicyDocButtons policy={p} />
            <CoiDialog policy={p} />
            {!upcoming && <ClaimDialog policies={[p]} />}
            {p.sr22_required === false ? null : ready ? (
              <Button asChild className="bg-emerald-600 text-white hover:bg-emerald-700 hover:text-white" data-testid="download-sr22-certificate-btn">
                <Link to={`/policies/${p.id}/certificate`} target="_blank"><Download className="mr-2 h-4 w-4" /> SR22 proof</Link>
              </Button>
            ) : (
              <Button disabled variant="outline" data-testid="download-sr22-certificate-btn">Certificate pending</Button>
            )}
          </div>
        </div>
        <div className="grid gap-6 p-6 sm:grid-cols-2 lg:grid-cols-4">
          <Item k="Insured" v={p.insured_name} />
          <Item k="Coverage" v={p.coverage_type === "general_liability" ? "Coverage A, B & C" : levelLabel(p.coverage_level)} />
          <Item k="Liability limits" v={limitLabel(p.liability_limit)} />
          {p.business_name && <Item k="Business" v={`${p.business_name} · ${p.coverage_type === "general_liability" ? `${p.employee_count || 1} employee(s)` : `${p.vehicle_count} vehicle(s)`}`} />}
          <Item k="Monthly premium" v={money(p.monthly_premium)} mono />
          {p.coverage_type !== "general_liability" && <Item k="Vehicle" v={p.vehicle || "Non-owner (any borrowed vehicle)"} />}
          {p.vin_last6 && <Item k="VIN (last 6)" v={p.vin_last6} mono />}
          <Item k="Effective" v={fmtDate(p.effective_date)} />
          <Item k="Expires" v={fmtDate(p.expiry_date)} />
          {p.filing_fee > 0 && <Item k="Filing fee paid" v={money(p.filing_fee)} mono />}
          <Item k="Policy status" v={p.status === "active" ? "Active" : "Cancelled"} />
        </div>
      </div>
      <section className="mt-8 rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-6 text-lg font-bold">{p.sr22_required === false ? "Policy status" : "SR22 filing status"}</h2>
        <FilingTimeline status={p.filing_status} sr22={p.sr22_required !== false} />
      </section>
      <BillingCard policyId={p.id} comped={p.comped} />
      <div className="mt-8"><DocumentsPanel policyId={p.id} /></div>
      <section className="mt-8 rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-lg font-bold">{p.sr22_required === false ? "Policy activity" : "Filing activity"}</h2>
        <ul className="space-y-3" data-testid="filing-history">
          {[...p.filing_history].reverse().map((h, i) => (
            <li key={i} className="flex gap-4 border-l-2 border-blue-200 pl-4">
              <div>
                <p className="text-sm font-semibold">{FILING_LABEL[h.status] || h.status}</p>
                <p className="font-mono text-xs text-slate-500">{new Date(h.at).toLocaleString()}</p>
                {h.note && <p className="mt-1 text-sm text-slate-600">{h.note}</p>}
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
